import React from "react";
import styled from "@emotion/styled";

/**
 * Numeric input that accepts negative values and any intermediate typing state.
 *
 * Why uncontrolled + ref:
 *   Controlled inputs (value prop) cause React to re-render the DOM node on every
 *   keystroke. When the user types "-", parseFloat("-") = NaN so the parent state
 *   doesn't update, but React still re-renders with the old numeric value and the
 *   "-" disappears. Using defaultValue (uncontrolled) means React never touches the
 *   input value while the user is typing. We sync the DOM imperatively via a ref
 *   only when the element is not focused (e.g. after an external save/reset).
 *
 * Lives here because the admin analytics tabs and the admin user-settings form
 * both need it — a second hand-rolled copy would drift.
 */
const NumericInputEl = styled.input`
  width: 100%;
  outline: none;
  padding: 0 12px 0 16px;
  height: 44px;
  background: #ffffff;
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #494c5b;
  box-sizing: border-box;
  border: 1px solid rgb(226 232 240);
  transition: 0.4s;
  :hover,
  :focus {
    border: 1px solid #899093;
  }
`;

interface IProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  placeholder?: string;
}

const NumericInput: React.FC<IProps> = ({ value, onChange, min, max, placeholder }) => {
  const ref = React.useRef<HTMLInputElement>(null);

  const clamp = (v: number) =>
    min !== undefined && v < min ? min : max !== undefined && v > max ? max : v;

  // Sync external value → DOM only when the field is not focused
  React.useEffect(() => {
    const el = ref.current;
    if (el && el !== document.activeElement) {
      el.value = String(value);
    }
  }, [value]);

  return (
    <NumericInputEl
      ref={ref}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      defaultValue={String(value)}
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(clamp(v));
      }}
      onBlur={(e) => {
        const v = parseFloat(e.target.value);
        if (isNaN(v)) {
          e.target.value = String(value); // restore last valid on invalid blur
        } else {
          const c = clamp(v);
          onChange(c);
          e.target.value = String(c);
        }
      }}
    />
  );
};

export default NumericInput;
